import Phaser from 'phaser';
import { SaveManager } from '../game/SaveManager';
import { t } from '../game/i18n';
import { drawGradientBackground } from '../ui/Background';
import { viewport } from '../ui/viewport';

/**
 * First-run profile setup: the human player names their team. Uses real DOM elements (a
 * <form> with an <input> and a submit button) so the mobile keyboard works AND the confirm
 * control stays reachable above the on-screen keyboard — both sit in the upper third, and
 * the keyboard's Go/Done key also submits.
 */
export class NameEntry extends Phaser.Scene {
  private form?: HTMLFormElement;
  private returnTo = 'MainMenu';

  constructor() {
    super('NameEntry');
  }

  init(data: { returnTo?: string }): void {
    this.returnTo = data?.returnTo ?? 'MainMenu';
  }

  create(): void {
    const { cx, h, s } = viewport(this);
    drawGradientBackground(this);

    this.add.text(cx, h * 0.09, `🏅 ${t('name.title')}`, {
      fontFamily: '"Twemoji Country Flags", system-ui', fontSize: `${Math.max(26, Math.round(38 * s))}px`, color: '#f4f7ff', fontStyle: 'bold',
    }).setOrigin(0.5).setShadow(0, 3, '#000000aa', 6);
    this.add.text(cx, h * 0.09 + Math.max(30, 40 * s), t('name.subtitle'), {
      fontFamily: '"Twemoji Country Flags", system-ui', fontSize: `${Math.max(14, Math.round(17 * s))}px`, color: '#a9b4d6',
    }).setOrigin(0.5);

    // DOM form, anchored in the upper area so the keyboard (bottom half) never covers it.
    const form = document.createElement('form');
    Object.assign(form.style, {
      position: 'fixed', left: '50%', top: '30%', transform: 'translateX(-50%)',
      display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px',
      width: 'min(86vw, 420px)', zIndex: '30',
    } satisfies Partial<CSSStyleDeclaration>);

    const input = document.createElement('input');
    input.type = 'text';
    input.maxLength = 24;
    input.placeholder = t('name.placeholder');
    input.value = SaveManager.instance.teamName;
    input.enterKeyHint = 'done';
    input.autocapitalize = 'words';
    Object.assign(input.style, {
      width: '100%', padding: '14px 16px', fontSize: '20px', fontFamily: '"Twemoji Country Flags", system-ui, sans-serif',
      borderRadius: '14px', border: '2px solid #59d2ff', background: '#0b1020', color: '#f4f7ff',
      textAlign: 'center', outline: 'none', boxSizing: 'border-box',
    } satisfies Partial<CSSStyleDeclaration>);

    const btn = document.createElement('button');
    btn.type = 'submit';
    btn.textContent = `✓  ${t('name.save').toUpperCase()}`;
    Object.assign(btn.style, {
      width: '100%', padding: '14px 16px', fontSize: '20px', fontWeight: '800',
      fontFamily: '"Twemoji Country Flags", system-ui, sans-serif', borderRadius: '14px', border: 'none',
      background: '#4ade80', color: '#0b1020', cursor: 'pointer',
      boxShadow: '0 4px 14px rgba(0,0,0,0.4)', touchAction: 'manipulation',
    } satisfies Partial<CSSStyleDeclaration>);

    form.appendChild(input);
    form.appendChild(btn);
    document.body.appendChild(form);
    this.form = form;
    setTimeout(() => input.focus(), 60);

    form.addEventListener('submit', (e) => { e.preventDefault(); this.confirm(input.value); });

    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => this.cleanup());
  }

  private confirm(value: string): void {
    const name = value.trim() || t('name.anon');
    SaveManager.instance.setTeamName(name);
    this.cleanup();
    this.scene.start(this.returnTo);
  }

  private cleanup(): void {
    this.form?.remove();
    this.form = undefined;
  }
}
